import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Send, ArrowLeft, Sparkles, Paperclip, X, FileText, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const META = {
  hustle: { name: "Hustle Assistant", placeholder: "Ask for a side-hustle idea, gig plan, or money-making tip…" },
  business: { name: "Business Builder", placeholder: "Ask for a business plan, marketing strategy, pricing model…" },
  everything: { name: "Everything Assistant", placeholder: "Ask anything — writing, coding, planning, life…" },
  ajo: { name: "Ajo Planner Chat", placeholder: "Ask about rotating savings groups, scheduling, fairness…" },
  document: { name: "Document Creator", placeholder: "Ask for a CV, cover letter, proposal, contract…" },
  brand: { name: "Brand & Logo Assistant", placeholder: "Ask for brand names, logo ideas, colour palettes, slogans…" },
  legal: { name: "AI Legal Helper", placeholder: "Ask about tenancy, employment, consumer rights, or letters…" },
};

export default function ChatPage() {
  const { assistantId } = useParams();
  const meta = META[assistantId] || META.everything;
  const [params, setParams] = useSearchParams();
  const initialSession = params.get("session") || null;

  const [sessionId, setSessionId] = useState(initialSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
      toast.error("Only PNG, JPG and PDF files are supported");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => [...f, data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // Load existing session messages
  useEffect(() => {
    if (!initialSession) return;
    (async () => {
      try {
        const { data } = await api.get(`/chat/sessions/${initialSession}`);
        setMessages(data.messages);
      } catch {
        toast.error("Could not load conversation");
      }
    })();
  }, [initialSession]);

  // Prefill from ?prompt= (assistant detail example prompts)
  useEffect(() => {
    const p = params.get("prompt");
    if (p) setInput(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    const text = input.trim();
    if ((!text && files.length === 0) || sending) return;
    const msgText = text || "Please analyse the attached file(s).";
    setSending(true);
    const optimistic = [...messages, { role: "user", content: msgText, attachments: files.map((f) => f.name), created_at: new Date().toISOString() }];
    setMessages(optimistic);
    setInput("");
    const sentFiles = files;
    setFiles([]);
    try {
      const { data } = await api.post("/chat", {
        assistant_id: assistantId,
        message: msgText,
        session_id: sessionId,
        file_ids: sentFiles.map((f) => f.file_id),
      });
      if (!sessionId) {
        setSessionId(data.session_id);
        setParams({ session: data.session_id }, { replace: true });
      }
      setMessages([...optimistic, { role: "assistant", content: data.reply, sources: data.sources || [], created_at: new Date().toISOString() }]);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err.response.data.detail, {
          action: { label: "Upgrade", onClick: () => window.location.assign("/pricing") },
          duration: 8000,
        });
      } else {
        toast.error(err?.response?.data?.detail || "Chat failed");
      }
      setMessages(messages);
      setFiles(sentFiles);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = useMemo(() => {
    if (messages.length > 0) return [];
    const map = {
      hustle: ["5 weekend side hustles under £100", "How can I monetise my graphic design skills?", "Best freelance platforms for beginners"],
      business: ["Write a one-page business plan for a smoothie bar", "Pricing strategy for my coaching service", "How do I validate my SaaS idea?"],
      everything: ["Explain quantum computing simply", "Draft a polite resignation email", "Help me plan a 3-day Lagos trip"],
      ajo: ["What is Ajo and how does it work?", "How do I keep an Ajo group fair?", "Best frequency for a 10-person Ajo"],
      document: ["Write a CV for a junior front-end developer", "Cover letter for a marketing internship", "Draft a freelance services contract"],
      brand: ["3 brand name ideas for a vegan bakery", "Logo concept for a fintech for students", "Colour palette + slogan for a fitness app"],
    };
    return map[assistantId] || map.everything;
  }, [assistantId, messages.length]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col px-4 py-6 md:px-6">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="chat-back-link">
          <ArrowLeft className="mr-1 h-4 w-4" /> Dashboard
        </Link>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Chatting with</div>
          <div className="font-display text-base">{meta.name}</div>
        </div>
      </div>

      <div className="flex min-h-[60vh] flex-1 flex-col rounded-2xl border border-border bg-card" data-testid="chat-window">
        {assistantId === "legal" && (
          <div className="border-b border-border bg-amber-500/5 px-4 py-2.5 text-[11px] leading-relaxed text-muted-foreground" data-testid="chat-legal-disclaimer">
            Zuntria is not a law firm and does not provide legal advice. This is AI-generated guidance and document drafting for information only. For legal advice, consult a qualified solicitor.
          </div>
        )}
        <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-6">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <h2 className="mt-6 font-display text-xl">How can I help today?</h2>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">{meta.placeholder}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    data-testid={`suggestion-${s.slice(0, 24)}`}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={`${m.created_at}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bubble-user rounded-br-sm" : "bubble-ai rounded-bl-sm"}`} data-testid={`message-${m.role}-${i}`}>
                {m.attachments?.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {m.attachments.map((name, j) => (
                      <span key={j} className="inline-flex items-center gap-1 rounded-md bg-black/20 px-2 py-0.5 text-[11px]">
                        <FileText className="h-3 w-3" /> {name}
                      </span>
                    ))}
                  </div>
                )}
                {typeof m.content === "string" ? m.content.replace(/\*\*/g, "").replace(/^(\s*)[*-]\s+/gm, "$1• ") : m.content}
                {m.sources?.length > 0 && (
                  <div className="mt-3 border-t border-border/50 pt-2" data-testid={`message-sources-${i}`}>
                    <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      <Globe className="h-3 w-3" /> Sources
                    </div>
                    <ol className="space-y-0.5 text-xs">
                      {m.sources.map((s, j) => (
                        <li key={j}>
                          <a href={s.url} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
                            [{j + 1}] {s.title || s.url}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bubble-ai inline-flex items-center gap-1 rounded-2xl rounded-bl-sm px-4 py-3">
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border p-3 md:p-4">
          {files.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5" data-testid="pending-attachments">
              {files.map((f) => (
                <span key={f.file_id} className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px]">
                  <FileText className="h-3 w-3 text-primary" /> {f.name}
                  <button onClick={() => setFiles((arr) => arr.filter((x) => x.file_id !== f.file_id))} data-testid={`remove-file-${f.file_id}`}>
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden" onChange={onPickFile} data-testid="chat-file-input" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading || files.length >= 5}
                    className="h-11 px-3" title="Attach PNG, JPG or PDF" data-testid="chat-attach-btn">
              <Paperclip className={`h-4 w-4 ${uploading ? "animate-pulse" : ""}`} />
            </Button>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={meta.placeholder}
              rows={1}
              className="min-h-[44px] resize-none"
              data-testid="chat-input"
            />
            <Button onClick={send} disabled={sending || uploading || (!input.trim() && files.length === 0)} className="h-11 px-4" data-testid="chat-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
